import type { RefObject } from 'react';
import { ResponsivePopover } from '../ResponsivePopover';
import { useDraftState } from '../../../hooks/useDraftState';
import type { TrackerStatus } from '@shared/flow/tracker';
import { AUTOFILL_IGNORE_PROPS } from '../../../lib/autofill';

/**
 * ONE popover for all three tracker statuses — prototype `trackerPop`
 * (docs/flow/custom-views-prototype.html:871). A segmented control picks
 * starred/snoozed/reminder; a date input renders ONLY for the two dated
 * statuses (starred needs none, R6); Save stays disabled until a required
 * date is present; Clear renders only when the record already has a saved
 * status (clearing an unset tracker is meaningless).
 *
 * Anchored via `ResponsivePopover` (desktop popover / mobile bottom sheet),
 * which itself uses `useAnchoredPosition` to escape the table's scroll box
 * — reused as-is, not rebuilt here.
 */

export interface TrackerPopoverProps {
  open: boolean;
  onClose: () => void;
  anchorRef: RefObject<HTMLElement | null>;
  /** Record identity — keys the draft (see `useDraftState` usage below). */
  recordId: string;
  /** Record name for the "Status · <name>" heading (prototype). */
  recordLabel?: string;
  /** Current saved tracker state — null when the record has none yet. */
  status: TrackerStatus | null;
  date: string | null;
  note: string | null;
  onSave: (input: { status: TrackerStatus; date: string | null; note: string | null }) => void;
  onClear: () => void;
  isSaving?: boolean;
  isClearing?: boolean;
}

interface TrackerDraft {
  status: TrackerStatus;
  date: string;
  note: string;
}

const STATUS_OPTIONS: { value: TrackerStatus; label: string }[] = [
  { value: 'starred', label: '★ Starred' },
  { value: 'snoozed', label: 'Snooze' },
  { value: 'reminder', label: 'Reminder' },
];

export function TrackerPopover({
  open, onClose, anchorRef, recordId, recordLabel, status, date, note, onSave, onClear, isSaving, isClearing,
}: TrackerPopoverProps) {
  // Draft state under SSE (docs/v4-system/draft-state-under-sse.md): every
  // editing surface in the v4 tree gets a parent re-render on ANY
  // `artifact:updated` SSE event, including invalidation refetches for the
  // record this popover has open. A bare useState + reseed effect keyed on
  // [open, status, date, note] would wipe the rep's in-progress draft the
  // moment a refetch resolves mid-edit — this is exactly that failure
  // signature, fixed via useDraftState's render-phase, identity-keyed seed.
  //
  // seedKey encodes BOTH the record identity and the open/closed session:
  // - `open` true  → seedKey === recordId. While the popover stays open on
  //   the SAME record, the key never changes, so SSE-driven prop churn
  //   (status/date/note taking on new values from a concurrent write) is
  //   ignored and the draft survives — the sanctioned Tier-2 behavior.
  // - `open` false → seedKey === `${recordId}::closed`, which differs from
  //   the open-session key. Reopening the SAME record therefore always
  //   re-applies the current seed (matches the pre-existing "discard the
  //   abandoned draft on reopen" contract this popover already had) and
  //   opening on a DIFFERENT record reseeds immediately, whether open or not.
  const seedKey = open ? recordId : `${recordId}::closed`;
  const seed: TrackerDraft = { status: status ?? 'starred', date: date ?? '', note: note ?? '' };
  const form = useDraftState<TrackerDraft>(seed, seedKey);
  const draft = form.draft ?? seed;

  const needsDate = draft.status !== 'starred';
  const canSave = !needsDate || draft.date.trim().length > 0;
  const hasExistingStatus = status != null;

  const handleSave = () => {
    if (!canSave) return;
    onSave({ status: draft.status, date: needsDate ? draft.date : null, note: draft.note.trim() || null });
  };

  return (
    <ResponsivePopover open={open} onClose={onClose} anchorRef={anchorRef} ariaLabel="Tracker status" className="w-[290px]">
      <div className="p-2.5">
        <div className="pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-content-tertiary dark:text-nightshade-500">
          Status{recordLabel ? ` · ${recordLabel}` : ''}
        </div>

        <div className="mb-2 flex gap-1" role="group" aria-label="Tracker status type">
          {STATUS_OPTIONS.map(opt => (
            <button
              key={opt.value}
              type="button"
              aria-pressed={draft.status === opt.value}
              onClick={() => form.setDraft({ status: opt.value })}
              className={`flex-1 whitespace-nowrap rounded-md border px-1.5 py-1 text-[11.5px] ${
                draft.status === opt.value
                  ? 'border-content-action bg-bg-blue-light text-content-action dark:border-blueberry-700 dark:text-blueberry-400'
                  : 'border-secondary text-content-secondary hover:bg-bg-secondary dark:border-nightshade-700 dark:text-nightshade-400 dark:hover:bg-nightshade-700'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {needsDate ? (
          <label className="mb-2 block">
            <span className="mb-1 block text-[11px] text-content-tertiary dark:text-nightshade-500">
              {draft.status === 'snoozed' ? 'Snooze until' : 'Remind me on'}{' '}
              <span className="text-content-danger dark:text-pomegranate-400">*</span>
            </span>
            <input
              {...AUTOFILL_IGNORE_PROPS}
              type="date"
              aria-label={draft.status === 'snoozed' ? 'Snooze until' : 'Remind me on'}
              value={draft.date}
              onChange={e => form.setDraft({ date: e.target.value })}
              className="w-full rounded-md border border-secondary bg-white px-2 py-1 text-[12px] text-content-primary dark:border-nightshade-700 dark:bg-nightshade-800 dark:text-nightshade-100"
            />
          </label>
        ) : (
          <div className="mb-2 text-[11px] text-content-tertiary dark:text-nightshade-500">
            Starred needs no date — it just floats to the top of Tracker.
          </div>
        )}

        <input
          {...AUTOFILL_IGNORE_PROPS}
          value={draft.note}
          onChange={e => form.setDraft({ note: e.target.value })}
          placeholder="Tracker note (why?) — Terra only"
          aria-label="Tracker note"
          className="mb-2 w-full rounded-md border border-secondary bg-white px-2 py-1 text-[12px] text-content-primary placeholder:text-content-placeholder dark:border-nightshade-700 dark:bg-nightshade-800 dark:text-nightshade-100"
        />

        <div className="mb-2.5 text-[11px] text-content-tertiary dark:text-nightshade-500">
          Snooze = you&rsquo;re deferring it. Reminder = they asked you to come back. Nothing here hides the row from anyone.
          Notes here stay in Terra — they don&rsquo;t sync to Salesforce.
        </div>

        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave || !!isSaving}
            className="flex-1 rounded-md bg-content-action px-2 py-1.5 text-[12px] font-medium text-white hover:bg-content-action-hover dark:bg-blueberry-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSaving ? 'Saving…' : 'Save'}
          </button>
          {hasExistingStatus && (
            <button
              type="button"
              onClick={onClear}
              disabled={!!isClearing}
              className="rounded-md border border-secondary px-2.5 py-1.5 text-[12px] text-content-secondary hover:bg-bg-secondary disabled:opacity-50 dark:border-nightshade-700 dark:text-nightshade-400 dark:hover:bg-nightshade-700"
            >
              {isClearing ? 'Clearing…' : 'Clear'}
            </button>
          )}
        </div>
      </div>
    </ResponsivePopover>
  );
}
